import {
  Chip,
  Input,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react'
import { format, parse } from 'date-fns'
import { FC } from 'react'
import {
  GradeTankValuesArrayType,
  TankValuesArrayType,
  UpdateSchoolValuesArrayType,
} from '../../types/apiResponse/tankTypes'
import { ColumnsArrayType } from './columns'
import { EventType, InputBase } from './Input'
import type { Group } from '@/types/domain'
import { GroupDropdown } from '../stock/GroupDropdown'

export type InsertTableProps = {
  tanksValues: TankValuesArrayType | UpdateSchoolValuesArrayType | GradeTankValuesArrayType
  inputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>
  columns: ColumnsArrayType
  insertData: (event: EventType) => void
  isGrade?: boolean
  groups?: Group[]
  groupSelections?: Record<number, { groupId: string | null; groupName: string | null }>
  onGroupChange?: (rowIndex: number, groupId: string | null, groupName: string | null) => void
  onGroupCreateNew?: (rowIndex: number) => void
  onBatchInputChange?: (rowIndex: number, value: string) => void
}

export const InsertTable: FC<InsertTableProps> = ({
  tanksValues,
  inputRefs,
  columns,
  insertData,
  isGrade = false,
  groups,
  groupSelections,
  onGroupChange,
  onGroupCreateNew,
  onBatchInputChange,
}) => {
  return (
    <Table
      isHeaderSticky
      aria-label="Data Field"
      radius="none"
      classNames={{
        base: 'overflow-auto min-h-[20rem] max-h-[20rem]',
        wrapper: 'min-h-[20rem] overflow-x-auto',
        td: 'p-0',
      }}
      data-testid="insert-data-table"
    >
      <TableHeader columns={columns}>
        {(column) => (
          <TableColumn
            className="font-bold text-center text-tdplprussian bg-tdplsky"
            key={column.key}
          >
            {column.label}
          </TableColumn>
        )}
      </TableHeader>
      <TableBody>
        {tanksValues.map((item, index) => (
          <TableRow
            key={item.tankId}
            className="border border-l-white border-r-white border-t-white border-b-divider data-[focus-visible=true]:outline-opacity-0"
          >
            {columns.map((cols, col_index) => (
              <TableCell
                key={item.tankId + col_index}
                className="py-0 data-[focus-visible=true]:outline-opacity-0"
                style={{ minWidth: cols.key === 'group' ? '180px' : `${cols.placeholder?.length + 1 || 0}ch`, minHeight: '2' }}
              >
                {cols.key === 'tankName' ? (
                  <div className="grid place-items-center">
                    <Chip
                      key={item.tankId}
                      radius="md"
                      classNames={{
                        base: `border-small border-tdpldarkgrey bg-${item.batchName === '$empty' ? 'tdpllightgrey' : 'tdplsky'} border-opacity-60 m-1 p-0`,
                        content: 'font-bold fit-content',
                      }}
                    >
                      {item.tankName}
                    </Chip>
                  </div>
                ) : cols.key === 'batchInput' && onBatchInputChange ? (
                  <Input
                    type="text"
                    size="sm"
                    radius="none"
                    value={'batchInput' in item ? (item as any).batchInput ?? '' : ''}
                    placeholder={cols.placeholder}
                    autoComplete="off"
                    onChange={(e) => onBatchInputChange(index, e.target.value)}
                    classNames={{
                      inputWrapper: 'p-0 shadow-none group-data-[focus-visible=true]:ring-opacity-0 bg-tdplbisque focus-within:!bg-tdplbisque',
                      base: 'm-0 p-0',
                      input: 'm-0 p-0 outline-none',
                    }}
                    style={{ textAlign: 'center' }}
                  />
                ) : cols.key === 'resultingCount' && 'resultingCount' in item && isGrade ? (
                  <p key={cols.key} style={{ textAlign: 'center' }}>
                    {Math.floor(Number(item.resultingCount) || 0)}
                  </p>
                ) : cols.key === 'resultingCount' && 'harvest' in item ? (
                  <p key={cols.key} style={{ textAlign: 'center' }}>
                    {Math.floor(
                      (Number(item.resultingCount) || 0) - (Number(item.harvest) || 0) - (Number(item.mortality) || 0),
                    )}
                  </p>
                ) : isGrade && cols.key === 'moveDate' && 'moveDate' in item ? (
                  <p key={cols.key} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {(() => {
                      const moveDate = item.moveDate
                      if (!moveDate || String(moveDate).length !== 8) return '—'
                      try {
                        const parsed = parse(String(moveDate), 'yyyyMMdd', new Date())
                        if (isNaN(parsed.getTime())) return '—'
                        return format(parsed, 'yyyy-MM-dd')
                      } catch {
                        return '—'
                      }
                    })()}
                  </p>
                ) : cols.key === 'group' && groups && onGroupChange && onGroupCreateNew ? (
                  <GroupDropdown
                    rowIndex={index}
                    selectedGroupId={groupSelections?.[index]?.groupId ?? null}
                    groups={groups}
                    onChange={onGroupChange}
                    onCreateNew={onGroupCreateNew}
                  />
                ) : (
                  <InputBase
                    key={item.tankId}
                    id={index}
                    inputRefs={inputRefs}
                    index={index * (columns.length - 1) + col_index - 1}
                    insertData={insertData}
                    col={cols.key}
                    value={item}
                    placeholder={cols.placeholder}
                  />
                )}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
